/**
 * UASCard — placeholder card for a UAS item in the short guide
 *
 * Matches the source doc format: UAS title, 2 lead-in questions,
 * Instructor RCA, primary competency. Flat style with burnt orange
 * left border. Below: 3 expandable ▶ links.
 */

import type { UASAnalysis } from '../../types/debriefing-guide';
import ExpandableSection from './ExpandableSection';
import CompetencyBadge from './CompetencyBadge';

interface UASCardProps {
  uas: UASAnalysis;
  index: number;
  expandedSections: Record<string, boolean>;
  onToggleSection: (sectionId: string) => void;
  renderQuestionLadder?: () => React.ReactNode;
  renderFiveWhy?: () => React.ReactNode;
  renderTEMBarrier?: () => React.ReactNode;
}

export default function UASCard({
  uas,
  index,
  expandedSections,
  onToggleSection,
  renderQuestionLadder,
  renderFiveWhy,
  renderTEMBarrier,
}: UASCardProps) {
  const prefix = `uas_${index}`;

  return (
    <div className="mb-3">
      {/* Placeholder card — burnt orange left border */}
      <div className="border-l-[3px] border-l-[#BF4E14] bg-white rounded-r-lg border border-l-0 border-steel/15 p-4">
        {/* UAS title + primary competency */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="text-sm font-medium text-gray-900">{uas.uasDescription}</p>
          <CompetencyBadge code={uas.primaryCompetency.code} name={uas.primaryCompetency.name} />
        </div>

        {/* 2 lead-in questions */}
        <div className="mb-2">
          {uas.questionLadder.slice(0, 2).map((q, qIdx) => (
            <p key={qIdx} className="text-sm text-gray-600 pl-3 border-l-2 border-gray-200 mb-1">
              {q}
            </p>
          ))}
        </div>

        {/* Instructor RCA */}
        <p className="text-xs text-gray-500">
          <span className="font-medium">Instructor RCA:</span> {uas.instructorRCASummary}
        </p>
      </div>

      {/* 3 expandable links below */}
      <div className="ml-1 mt-0.5">
        <ExpandableSection
          label="Click to view question ladder & resilience/recovery prompts for this UAS"
          expanded={!!expandedSections[`${prefix}_question_ladder`]}
          onToggle={() => onToggleSection(`${prefix}_question_ladder`)}
        >
          {renderQuestionLadder ? renderQuestionLadder() : <QuestionLadderContent uas={uas} />}
        </ExpandableSection>

        <ExpandableSection
          label="Click to view 5-Why logic template & UAS description fields for this UAS"
          expanded={!!expandedSections[`${prefix}_five_why`]}
          onToggle={() => onToggleSection(`${prefix}_five_why`)}
        >
          {renderFiveWhy ? renderFiveWhy() : <FiveWhyContent uas={uas} />}
        </ExpandableSection>

        <ExpandableSection
          label="Click to view TEM barrier layer for the primary competency in this UAS"
          expanded={!!expandedSections[`${prefix}_tem_barrier`]}
          onToggle={() => onToggleSection(`${prefix}_tem_barrier`)}
        >
          {renderTEMBarrier ? renderTEMBarrier() : <TEMBarrierContent uas={uas} />}
        </ExpandableSection>
      </div>
    </div>
  );
}

function QuestionLadderContent({ uas }: { uas: UASAnalysis }) {
  return (
    <div className="space-y-3">
      <div>
        <h5 className="text-xs font-semibold text-steel mb-1">Full Question Ladder</h5>
        <ol className="list-decimal list-inside text-sm space-y-1">
          {uas.questionLadder.map((q, i) => (
            <li key={i} className="text-gray-700">{q}</li>
          ))}
        </ol>
      </div>
      {uas.resiliencePrompts && uas.resiliencePrompts.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-steel mb-1">Resilience/Recovery Prompts</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            {uas.resiliencePrompts.map((p, i) => (
              <li key={i} className="text-green-700">{p}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FiveWhyContent({ uas }: { uas: UASAnalysis }) {
  return (
    <div className="space-y-3">
      <div>
        <h5 className="text-xs font-semibold text-steel mb-1">UAS Description</h5>
        <p className="text-sm text-gray-700">{uas.uasDescription}</p>
      </div>
      <div>
        <h5 className="text-xs font-semibold text-steel mb-1">5-Why Logic Chain</h5>
        <ol className="list-decimal list-inside text-sm space-y-1">
          {uas.fiveWhyChain.map((why, i) => (
            <li key={i} className="text-gray-700">{why}</li>
          ))}
        </ol>
      </div>
      <div className="bg-red-50 border border-red-200 rounded p-3">
        <span className="text-xs font-medium text-red-600">
          Instructor Preparation Only — Not to be stated as conclusions
        </span>
        <p className="text-sm text-red-800 mt-1">{uas.instructorRCASummary}</p>
      </div>
    </div>
  );
}

function TEMBarrierContent({ uas }: { uas: UASAnalysis }) {
  return (
    <div>
      <h5 className="text-xs font-semibold text-steel mb-2">
        Primary Competency: {uas.primaryCompetency.code} — {uas.primaryCompetency.name}
      </h5>
      <p className="text-sm text-gray-700 mb-2">{uas.primaryCompetency.behavioralDescription}</p>
      {uas.primaryCompetency.observableBehaviors.length > 0 && (
        <div className="mt-2">
          <span className="text-xs text-steel">Observable Behaviors:</span>
          <ul className="list-disc list-inside text-sm mt-1 space-y-1">
            {uas.primaryCompetency.observableBehaviors.map((ob, i) => (
              <li key={i} className="text-gray-600">
                <span className="font-mono text-xs text-steel">{ob.code}</span>{' '}
                {ob.description}
              </li>
            ))}
          </ul>
        </div>
      )}
      {uas.countermeasures.length > 0 && (
        <div className="mt-3">
          <span className="text-xs font-semibold text-steel">Countermeasures:</span>
          <ul className="list-disc list-inside text-sm mt-1">
            {uas.countermeasures.map((cm, i) => (
              <li key={i} className="text-green-700">{cm}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
